import { useEffect, useLayoutEffect, useRef } from "react";
import type { RefObject } from "react";

// A notch on a mouse wheel, and a page, in the pixels the other modes report.
const LINE_HEIGHT = 16;

const PAGE_HEIGHT = 400;

// Gentle enough that one notch changes the window by about a fifth.
const ZOOM_SENSITIVITY = 0.002;

export type ZoomGestures = {
  enabled: boolean;
  anchorAt: (clientX: number) => number;
  zoomAt: (anchor: number, factor: number) => void;
  panBy: (minutes: number) => void;
  span: number;
};

/**
 * Wheel zoom on an element: Ctrl or Cmd held, which is also what a trackpad
 * pinch sends, and panning on Shift or a horizontal wheel. A bare wheel is
 * left alone, so the page scrolls as it always did.
 *
 * React registers `wheel` passively, so the listener is attached by hand:
 * `preventDefault` is what keeps the browser from zooming the page instead.
 */
export function useZoomGestures(
  ref: RefObject<HTMLElement | null>,
  gestures: ZoomGestures,
): void {
  const latest = useRef(gestures);

  useLayoutEffect(() => {
    latest.current = gestures;
  }, [gestures]);

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    const handleWheel = (event: WheelEvent) => {
      const { enabled, anchorAt, zoomAt, panBy, span } = latest.current;

      if (!enabled) {
        return;
      }

      const scale =
        1 === event.deltaMode
          ? LINE_HEIGHT
          : 2 === event.deltaMode
            ? PAGE_HEIGHT
            : 1;

      const deltaY = event.deltaY * scale;
      const deltaX = event.deltaX * scale;

      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        zoomAt(anchorAt(event.clientX), Math.exp(deltaY * ZOOM_SENSITIVITY));

        return;
      }

      const horizontal = Math.abs(deltaX) > Math.abs(deltaY);

      if (event.shiftKey || horizontal) {
        event.preventDefault();

        const travel = horizontal ? deltaX : deltaY;

        panBy((travel / element.clientWidth) * span);
      }
    };

    element.addEventListener("wheel", handleWheel, { passive: false });

    return () => element.removeEventListener("wheel", handleWheel);
  }, [ref]);
}
